/**
 * Revenue Analytics Hook
 *
 * INVARIANTS (DO NOT VIOLATE):
 * 1. Booking Time Model:
 *    - booking_date = naive DATE (business wall-time)
 *    - booking_time = naive TIME
 *    - Use format(date, 'yyyy-MM-dd') for DATE columns (NEVER .toISOString())
 *
 * 2. Revenue Model:
 *    - gross_amount = service_price + tip_amount (what the customer paid)
 *    - net_amount = gross_amount - processing_fee - platform_fee (provider proceeds before refunds)
 *    - provider_refund_amount = amount actually reversed from provider's transfer
 *    - realized_provider_earnings = net_amount - provider_refund_amount
 *    - Dashboard metrics = current month pipeline (past + future), paid only
 *    - Disputed/lost bookings are excluded from realized earnings
 *
 * 3. Query Bounds:
 *    - "Current month" = [start of month, start of next month)
 *    - Use DATE strings, not TIMESTAMPTZ
 */
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';

export interface RevenueData {
  totalRevenue: number;
  netRevenue: number;
  grossRevenue: number;
  totalRefunds: number;
  feeRevenue: number;
  totalBookings: number;
  activeClients: number;
  averageBookingValue: number;
  revenueGrowth: number;
  bookingsGrowth: number;
  upcomingRevenue: number;
}


export interface MonthlyRevenue {
  month: string;
  revenue: number;
  bookings: number;
}

export const useRevenueAnalytics = () => {
  const [data, setData] = useState<RevenueData | null>(null);
  const [monthlyData, setMonthlyData] = useState<MonthlyRevenue[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRevenueData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      // Get business profile
      const { data: businessProfile } = await supabase
        .from('business_profiles')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (!businessProfile) throw new Error('No business profile found');

      const businessId = businessProfile.id;
      const now = new Date();
      const currentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);

      // Date strings for DATE column filters (not TIMESTAMPTZ)
      const monthStartStr = format(currentMonth, 'yyyy-MM-dd');
      const nextMonthStr = format(nextMonth, 'yyyy-MM-dd');
      const lastMonthStr = format(lastMonth, 'yyyy-MM-dd');
      const lastMonthEndStr = format(lastMonthEnd, 'yyyy-MM-dd');
      const todayStr = format(now, 'yyyy-MM-dd');

      // Get current month bookings data (includes future bookings for pipeline visibility)
      // Current month = [monthStartStr, nextMonthStr)
      const { data: currentBookings, error: bookingsError } = await supabase
        .from('bookings')
        .select('id, client_id, payment_status, status, net_amount, provider_refund_amount, refund_amount, gross_amount, dispute_status, transfer_status')
        .eq('business_id', businessId)
        .in('payment_status', ['paid', 'refunded'])
        .in('status', ['confirmed', 'completed'])
        .gte('booking_date', monthStartStr)
        .lt('booking_date', nextMonthStr);

      if (bookingsError) {
        console.warn('Current bookings query failed:', bookingsError.message);
      }

      // Get last month bookings data for growth calculation
      const { data: lastMonthBookings, error: lastMonthError } = await supabase
        .from('bookings')
        .select('id, status, net_amount, provider_refund_amount, refund_amount, dispute_status, transfer_status')
        .eq('business_id', businessId)
        .in('payment_status', ['paid', 'refunded'])
        .in('status', ['confirmed', 'completed'])
        .gte('booking_date', lastMonthStr)
        .lte('booking_date', lastMonthEndStr);
      
      if (lastMonthError) {
        console.warn('Last month bookings query failed:', lastMonthError.message);
      }

      // Get no-show fee revenue (current month)
      const { data: currentFees } = await supabase
        .from('no_show_fees')
        .select('amount')
        .in('status', ['charged', 'paid'])
        .gte('created_at', currentMonth.toISOString())
        .lte('created_at', now.toISOString());

      // Get last month fees for growth calculation
      const { data: lastMonthFees } = await supabase
        .from('no_show_fees')
        .select('amount')
        .in('status', ['charged', 'paid'])
        .gte('created_at', lastMonth.toISOString())
        .lte('created_at', lastMonthEnd.toISOString());

      // Get upcoming revenue (future paid bookings, including today)
      const { data: upcomingBookings } = await supabase
        .from('bookings')
        .select('net_amount, provider_refund_amount, refund_amount, dispute_status, transfer_status')
        .eq('business_id', businessId)
        .in('payment_status', ['paid', 'refunded'])
        .in('status', ['confirmed', 'completed'])
        .gte('booking_date', todayStr);

      // Helper: check if booking should be excluded from realized earnings.
      // - Disputed or lost bookings: earnings are disputed/unrecoverable
      // - Failed transfers: provider never received funds
      const hasZeroRealizedEarnings = (booking: any) =>
        booking.dispute_status === 'disputed' ||
        booking.dispute_status === 'resolved_lost' ||
        booking.transfer_status === 'failed';

      // Calculate current month metrics
      const netRevenue = currentBookings?.reduce((sum, booking) => {
        if (hasZeroRealizedEarnings(booking)) return sum;
        const net = Number(booking.net_amount || 0);
        const providerRefund = Number(booking.provider_refund_amount || 0);
        const realized = net - providerRefund;
        return sum + Math.max(0, realized);
      }, 0) || 0;
      const totalRefunds = currentBookings?.reduce((sum, booking) => sum + Number(booking.refund_amount || 0), 0) || 0;
      const grossRevenue = currentBookings?.reduce((sum, booking) => sum + Number(booking.gross_amount || 0), 0) || 0;
      const feeRevenue = currentFees?.reduce((sum, fee) => sum + Number(fee.amount || 0), 0) || 0;
      const totalRevenue = netRevenue + feeRevenue; // Provider earnings = net booking revenue + fees

      // Exclude bookings with zero realized earnings from counts consistently with revenue
      const activeBookings = currentBookings?.filter(b => !hasZeroRealizedEarnings(b)) || [];
      const totalBookings = activeBookings.length;

      // Calculate unique clients (from active bookings only)
      const uniqueClients = new Set(activeBookings.map(b => b.client_id)).size;

      // Calculate average booking value (numerator and denominator both exclude disputed)
      const averageBookingValue = totalBookings > 0 ? netRevenue / totalBookings : 0;

      // Calculate upcoming revenue (future paid bookings)
      const upcomingRevenue = upcomingBookings?.reduce((sum, booking) => {
        if (hasZeroRealizedEarnings(booking)) return sum;
        const net = Number(booking.net_amount || 0);
        const providerRefund = Number(booking.provider_refund_amount || 0);
        return sum + Math.max(0, net - providerRefund);
      }, 0) || 0;

      // Calculate last month metrics for growth
      const lastMonthNetRevenue = lastMonthBookings?.reduce((sum, booking) => {
        if (hasZeroRealizedEarnings(booking)) return sum;
        const net = Number(booking.net_amount || 0);
        const providerRefund = Number(booking.provider_refund_amount || 0);
        return sum + Math.max(0, net - providerRefund);
      }, 0) || 0;
      const lastMonthFeeRevenue = lastMonthFees?.reduce((sum, fee) => sum + Number(fee.amount || 0), 0) || 0;
      const lastMonthTotalRevenue = lastMonthNetRevenue + lastMonthFeeRevenue;
      const lastMonthActiveBookings = lastMonthBookings?.filter(b => !hasZeroRealizedEarnings(b)) || [];
      const lastMonthBookingsCount = lastMonthActiveBookings.length;

      // Calculate growth percentages
      const revenueGrowth = lastMonthTotalRevenue > 0 
        ? ((totalRevenue - lastMonthTotalRevenue) / lastMonthTotalRevenue) * 100 
        : totalRevenue > 0 ? 100 : 0;
      
      const bookingsGrowth = lastMonthBookingsCount > 0 
        ? ((totalBookings - lastMonthBookingsCount) / lastMonthBookingsCount) * 100 
        : totalBookings > 0 ? 100 : 0;

      // Get monthly data for the last 6 months
      const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 5, 1);
      const sixMonthsAgoStr = format(sixMonthsAgo, 'yyyy-MM-dd');

      const { data: monthlyBookings, error: monthlyError } = await supabase
        .from('bookings')
        .select('id, status, net_amount, provider_refund_amount, refund_amount, booking_date, dispute_status, transfer_status')
        .eq('business_id', businessId)
        .in('payment_status', ['paid', 'refunded'])
        .in('status', ['confirmed', 'completed'])
        .gte('booking_date', sixMonthsAgoStr)
        .lt('booking_date', nextMonthStr)
        .order('booking_date');
      
      if (monthlyError) {
        console.warn('Monthly bookings query failed:', monthlyError.message);
      }

      // Get monthly fees
      const { data: monthlyFees } = await supabase
        .from('no_show_fees')
        .select('amount, created_at')
        .in('status', ['charged', 'paid'])
        .gte('created_at', sixMonthsAgo.toISOString())
        .lte('created_at', now.toISOString());

      // Group by month
      const monthlyMap = new Map<string, { revenue: number; bookings: number }>();
      
      // Initialize months
      for (let i = 5; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const key = date.toISOString().slice(0, 7); // YYYY-MM format
        monthlyMap.set(key, { revenue: 0, bookings: 0 });
      }

      // Add booking data
      monthlyBookings?.forEach(booking => {
        if (hasZeroRealizedEarnings(booking)) return;
        const month = booking.booking_date.slice(0, 7);
        const current = monthlyMap.get(month) || { revenue: 0, bookings: 0 };
        const net = Number(booking.net_amount || 0);
        const providerRefund = Number(booking.provider_refund_amount || 0);
        current.revenue += Math.max(0, net - providerRefund);
        current.bookings += 1;
        monthlyMap.set(month, current);
      });

      // Add fee data
      monthlyFees?.forEach(fee => {
        const month = fee.created_at.slice(0, 7);
        const current = monthlyMap.get(month) || { revenue: 0, bookings: 0 };
        current.revenue += Number(fee.amount || 0);
        monthlyMap.set(month, current);
      });

      const monthlyData: MonthlyRevenue[] = Array.from(monthlyMap.entries()).map(([month, data]) => ({
        month: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        revenue: data.revenue,
        bookings: data.bookings
      }));

      // Tripwire: Dashboard sanity check (dev-only, non-blocking)
      const upcomingAppointmentsCount = upcomingBookings?.length || 0;
      if (upcomingAppointmentsCount > 0 && totalBookings === 0) {
        console.warn('[Dashboard Tripwire] Inconsistency detected: upcoming appointments exist but total bookings = 0', {
          upcomingAppointmentsCount,
          totalBookings
        });
      }

      setData({
        totalRevenue,
        netRevenue,
        grossRevenue,
        totalRefunds,
        feeRevenue,
        totalBookings,
        activeClients: uniqueClients,
        averageBookingValue,
        revenueGrowth,
        bookingsGrowth,
        upcomingRevenue
      });

      setMonthlyData(monthlyData);

    } catch (err) {
      console.error('Revenue analytics error:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch revenue data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRevenueData();
  }, []);

  return {
    data,
    monthlyData,
    isLoading,
    error,
    refetch: fetchRevenueData
  };
};